/**
 * Arranque temprano de las dos descargas grandes del sitio, antes del JS.
 *
 * El contenido del sitio se arma en el navegador con dos respuestas de
 * Supabase que no dependen de nada más que del tenant: el árbol del builder
 * (`client_website_config.elements_structure`) y el inventario (`vehicles`).
 * Hasta acá las dos salían recién cuando el bundle de React terminaba de
 * bajar, ejecutarse e hidratar (~1 s después del HTML). El servidor ya sabe
 * qué tenant es, así que puede dejar en el `<head>` un script de cinco líneas
 * que dispare esos dos `fetch` apenas el navegador lee el HTML, y el código de
 * siempre los recoge en vez de volver a pedirlos.
 *
 * Es una optimización de orden, no de datos: las URLs son exactamente las que
 * construye supabase-js (mismo `select`, mismos filtros, mismo `order`), y si
 * la respuesta temprana falla o no coincide, el consumidor hace su request de
 * siempre. La clave anónima ya viaja en el bundle público; acá no se expone
 * nada nuevo.
 *
 * Lado servidor: `scriptDePrefetch` (va al `<head>` del layout).
 * Lado navegador: `tomarPrefetch` (lo llaman el store y el ClientProvider).
 */

import { VEHICLE_INVENTORY_SELECT } from '@/lib/inventory-select';
import { ORDEN_TENANT_POR_DEFECTO, ordenInventarioRest, type OrdenWebTenant } from '@/lib/orden-vehiculos';

export const CLAVE_VENTANA = '__goautoPrefetch';

type Recurso = 'vehicles' | 'elements_structure';

interface Entrada {
  clave: string;
  promise: Promise<unknown>;
}

/** PostgREST recibe el `select` sin espacios: es lo mismo que hace supabase-js. */
export const selectSinEspacios = (select: string): string => select.replace(/\s+/g, '');

/** Clave con la que el store reconoce "esta es la descarga que yo habría hecho". */
export const claveInventario = (clientId: string | number, hasDemo: boolean): string =>
  `${String(clientId)}|${hasDemo ? 1 : 0}`;

/** URL REST del inventario, idéntica a la de `useVehiclesStore.fetchVehicles`. */
export function urlInventario(
  supabaseUrl: string,
  clientId: string | number,
  hasDemo: boolean,
  orden: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO
): string {
  const p = new URLSearchParams();
  p.set('select', selectSinEspacios(VEHICLE_INVENTORY_SELECT));
  if (hasDemo) p.set('or', `(client_id.eq.${clientId},client_id.eq.1)`);
  else p.set('client_id', `eq.${clientId}`);
  // Ticket #164: el mismo orden que arma el store, desde el módulo único
  // (`lib/orden-vehiculos`) y con la preferencia de ESTA automotora. Si acá
  // quedara un orden fijo, la respuesta precargada vendría ordenada distinto
  // que la del store y la configuración se perdería justo en la carga rápida,
  // que es la que ve casi todo el mundo.
  p.set('order', ordenInventarioRest(orden));
  return `${supabaseUrl}/rest/v1/vehicles?${p.toString()}`;
}

/** URL REST del árbol del builder, idéntica a la del `ClientProvider`. */
export function urlEstructura(supabaseUrl: string, cfgId: string): string {
  const p = new URLSearchParams();
  p.set('select', 'id,elements_structure');
  p.set('id', `eq.${cfgId}`);
  return `${supabaseUrl}/rest/v1/client_website_config?${p.toString()}`;
}

/**
 * El script inline. Devuelve '' si falta algo (sin tenant, sin URL de
 * Supabase): en ese caso el sitio se comporta exactamente como antes.
 */
export function scriptDePrefetch(opts: {
  supabaseUrl?: string;
  anonKey?: string;
  clientId?: string | number | null;
  hasDemo?: boolean;
  /** Orden inicial configurado por la automotora (`clients.web_vehicle_order`). */
  orden?: OrdenWebTenant;
  cfgId?: string | null;
}): string {
  const { supabaseUrl, anonKey, clientId, hasDemo = false, orden = ORDEN_TENANT_POR_DEFECTO, cfgId } = opts;
  if (!supabaseUrl || !anonKey || clientId == null || clientId === '') return '';

  const entradas: Array<{ nombre: Recurso; clave: string; url: string }> = [
    { nombre: 'vehicles', clave: claveInventario(clientId, hasDemo), url: urlInventario(supabaseUrl, clientId, hasDemo, orden) },
  ];
  if (cfgId) {
    entradas.push({ nombre: 'elements_structure', clave: String(cfgId), url: urlEstructura(supabaseUrl, cfgId) });
  }

  // JSON.stringify: todo lo que se interpola queda entre comillas y escapado,
  // así que no hay forma de que un valor del tenant rompa el script.
  const lista = JSON.stringify(entradas);
  const headers = JSON.stringify({
    apikey: anonKey,
    Authorization: `Bearer ${anonKey}`,
    Accept: 'application/json',
  });
  // La ficha de un vehículo no usa el inventario (misma regla que en
  // ClientProvider): ahí no se adelanta esa descarga.
  return (
    `(function(){try{var w=window,k=${JSON.stringify(CLAVE_VENTANA)};if(!w.fetch)return;var m=w[k]=w[k]||{};` +
    `var ficha=/^\\/vehicles\\/[^/]+/.test(w.location.pathname);` +
    `${lista}.forEach(function(e){if(e.nombre==='vehicles'&&ficha)return;var p=w.fetch(e.url,{headers:${headers}})` +
    `.then(function(r){if(!r.ok)throw new Error('HTTP '+r.status);return r.json();});` +
    // El consumidor se engancha recién al hidratar: sin este catch, un fallo
    // de red previo quedaría como "Uncaught (in promise)" en la consola y en
    // PostHog aunque el fallback funcione. El catch derivado marca el rechazo
    // como manejado; `p` sigue rechazando para quien lo consuma.
    `p.catch(function(){});m[e.nombre]={clave:e.clave,promise:p};});}catch(e){}})();`
  );
}

/**
 * Recoge (una sola vez) la respuesta temprana de un recurso, si existe y es
 * la que el consumidor habría pedido. `null` = no hay: pedir como siempre.
 */
export function tomarPrefetch<T = unknown>(nombre: Recurso, clave: string): Promise<T> | null {
  if (typeof window === 'undefined') return null;
  const mapa = (window as unknown as Record<string, Record<string, Entrada> | undefined>)[CLAVE_VENTANA];
  const entrada = mapa?.[nombre];
  if (!entrada || entrada.clave !== clave) return null;
  delete mapa![nombre];
  return entrada.promise as Promise<T>;
}
